/**
 * Bookings that arrived on their own, waiting to be looked at.
 *
 *   "Is it possible to have an icon next to our bell specific for
 *    notifications of online bookings. We would click on it so lets say two
 *    ppl booked for a Thursday on a Monday night and we come in on Tuesday
 *    see the online booking bell and we are able to see the booking and be
 *    alerted to it once we clear the booking we remove from the list so it
 *    would need remove button no sound for that one"
 *
 * Deliberately NOT part of the doorbell next to it: that one rings, and this
 * one must not. It is also not a count of today — a booking made on Monday
 * night for Thursday is still sitting here on Tuesday morning, because the
 * only thing that empties this list is somebody clearing it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Loader2, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

interface Enquiry {
  id: string;
  contact_id: string;
  client_name: string;
  created_at: string;
  source: string;
  phone: string | null;
  email: string | null;
  message: string | null;
}

interface OnlineBooking {
  id: string;
  title: string;
  starts_at: string;
  created_at: string;
  status: string;
  contact_id: string | null;
  client_name: string;
  room: string | null;
  previous_visits: number;
}

const POLL_MS = 60_000;

export function OnlineBookingBell() {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<OnlineBooking[]>([]);
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [tz, setTz] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    api.get<{ bookings: OnlineBooking[]; enquiries: Enquiry[]; timezone: string }>("/frontdesk/online-bookings")
      .then((d) => {
        setItems(d.bookings ?? []);
        setEnquiries(d.enquiries ?? []);
        setTz(d.timezone ?? null);
      })
      .catch(() => { /* the badge simply stays as it was */ })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const clear = async (id?: string) => {
    setBusy(id ?? "all");
    try {
      await api.post("/frontdesk/online-bookings/clear", id ? { id } : {});
      setItems((prev) => (id ? prev.filter((x) => x.id !== id) : []));
      // Clear all empties the enquiries with them: one button, one list.
      if (!id) {
        await api.post("/frontdesk/enquiries/clear", {});
        setEnquiries([]);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't clear that");
    } finally { setBusy(null); }
  };

  const clearEnquiry = async (id: string) => {
    setBusy(id);
    try {
      await api.post("/frontdesk/enquiries/clear", { id });
      setEnquiries((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't clear that");
    } finally { setBusy(null); }
  };

  const when = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", timeZone: tz ?? undefined,
      }).format(new Date(iso));
    } catch { return iso; }
  };

  const n = items.length + enquiries.length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={n > 0 ? `${n} online booking${n === 1 ? "" : "s"} or enquir${n === 1 ? "y" : "ies"} to look at` : "Online bookings and enquiries"}
        className="relative rounded-lg p-2 text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200"
      >
        <CalendarPlus className="h-5 w-5" />
        {n > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-emerald-500 px-1 text-[10px] font-semibold text-white">
            {n > 9 ? "9+" : n}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-80 max-w-[92vw] animate-fade-in rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-black/40">
          <div className="flex items-center justify-between gap-2 border-b border-zinc-800 px-3 py-2.5">
            <p className="text-sm font-semibold text-zinc-100">Booked online &amp; enquiries</p>
            {n > 0 && (
              <button type="button" disabled={busy === "all"} onClick={() => void clear()}
                className="text-[11px] font-medium text-zinc-500 hover:text-zinc-300 disabled:opacity-50">
                Clear all
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <p className="flex items-center gap-2 px-3 py-4 text-sm text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Looking…
              </p>
            ) : n === 0 ? (
              <p className="px-3 py-4 text-sm text-zinc-500">Nothing new. Anything booked online, and anybody who fills in an enquiry form, lands here.</p>
            ) : (
              <ul className="divide-y divide-zinc-800/60">
                {enquiries.map((e) => (
                  <li key={e.id} className="flex items-start gap-2 bg-amber-500/5 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-100">
                        {/* "Also Matt where is this message from Alicia I can't
                            find it" - she could see the first line of it right
                            here and still had to go hunting. The name now lands
                            on their Messages, where the enquiry is the first
                            thing in the thread and she can just reply. */}
                        <Link to={`/contacts/${e.contact_id}?tab=messages`} onClick={() => setOpen(false)}
                          className="truncate hover:text-indigo-300">{e.client_name}</Link>
                        <span className="shrink-0 rounded bg-amber-500/15 px-1 text-[10px] font-semibold text-amber-300">
                          Enquiry
                        </span>
                      </p>
                      <p className="truncate text-[11px] text-zinc-400">
                        {e.source.replace(/-/g, " ")}
                        {e.phone ? ` · ${e.phone}` : e.email ? ` · ${e.email}` : ""}
                      </p>
                      {e.message && (
                        <p className="mt-0.5 line-clamp-2 text-[11px] text-zinc-500">{e.message}</p>
                      )}
                      <p className="text-[11px] text-zinc-600">came in {formatRelative(e.created_at)}</p>
                    </div>
                    <button
                      type="button"
                      disabled={busy === e.id}
                      onClick={() => void clearEnquiry(e.id)}
                      title="Remove from this list"
                      aria-label={`Remove ${e.client_name} from this list`}
                      className="shrink-0 rounded p-1 text-zinc-600 hover:bg-zinc-800 hover:text-zinc-300 disabled:opacity-50"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
                {items.map((b) => (
                  <li key={b.id} className="flex items-start gap-2 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-100">
                        {b.contact_id ? (
                          <Link to={`/contacts/${b.contact_id}`} onClick={() => setOpen(false)}
                            className="truncate hover:text-indigo-300">{b.client_name}</Link>
                        ) : <span className="truncate">{b.client_name}</span>}
                        {b.previous_visits === 0 && (
                          <span className="shrink-0 rounded bg-amber-500/15 px-1 text-[10px] font-semibold text-amber-300">New</span>
                        )}
                      </p>
                      <p className="truncate text-[11px] text-zinc-400">{b.title}</p>
                      <p className="text-[11px] text-zinc-500">
                        {when(b.starts_at)}{b.room ? ` · ${b.room}` : ""}
                      </p>
                      <p className="text-[11px] text-zinc-600">booked {formatRelative(b.created_at)}</p>
                    </div>
                    <button
                      type="button"
                      disabled={busy === b.id}
                      onClick={() => void clear(b.id)}
                      title="Remove from this list"
                      aria-label={`Remove ${b.client_name} from this list`}
                      className={cn(
                        "shrink-0 rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200",
                        busy === b.id && "opacity-50",
                      )}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
